/**
 * services/vendorService.ts - Vendor API Service
 *
 * Handles all vendor-related API calls
 */

import apiClient from './apiClient';
import { Vendor, FoodMenuItem } from '../utils/constants';

export interface MenuItem extends FoodMenuItem {
  category?: string;
  available?: boolean;
}

export type CreateMenuItemInput = Omit<FoodMenuItem, 'id'> & {
  category?: string;
  available?: boolean;
};

export type UpdateMenuItemInput = Partial<CreateMenuItemInput>;

function mapVendor(vendor: Record<string, unknown>): Vendor {
  return {
    ...(vendor as unknown as Vendor),
    id: String(vendor.id ?? ''),
    revenue: typeof vendor.revenue === 'number' ? vendor.revenue : 0,
    menuItems: (vendor.menuItems as FoodMenuItem[]) || [],
  };
}

class VendorService {
  private basePath = '/vendors';

  async getAll(): Promise<Vendor[]> {
    try {
      const vendors = await apiClient.get<Record<string, unknown>[]>(`${this.basePath}?includeInactive=true`);
      return vendors.map(mapVendor);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<Vendor> {
    try {
      const vendor = await apiClient.get<Record<string, unknown>>(
        `${this.basePath}/${id}?includeInactive=true`,
      );
      return mapVendor(vendor);
    } catch (error) {
      console.error('Error fetching vendor:', error);
      throw error;
    }
  }

  async create(vendorData: { name: string; sections: string[] }): Promise<Vendor> {
    try {
      const vendor = await apiClient.post<Record<string, unknown>>(this.basePath, vendorData);
      return mapVendor(vendor);
    } catch (error) {
      console.error('Error creating vendor:', error);
      throw error;
    }
  }

  async update(id: string, vendorData: Partial<Vendor>): Promise<Vendor> {
    try {
      const vendor = await apiClient.put<Record<string, unknown>>(`${this.basePath}/${id}`, vendorData);
      return mapVendor(vendor);
    } catch (error) {
      console.error('Error updating vendor:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.basePath}/${id}`);
    } catch (error) {
      console.error('Error deleting vendor:', error);
      throw error;
    }
  }

  async addMenuItem(vendorId: string, menuItem: CreateMenuItemInput): Promise<Vendor> {
    try {
      const vendor = await apiClient.post<Record<string, unknown>>(
        `${this.basePath}/${vendorId}/menu-items`,
        menuItem
      );
      return mapVendor(vendor);
    } catch (error) {
      console.error('Error adding menu item:', error);
      throw error;
    }
  }

  async updateMenuItem(
    vendorId: string,
    itemId: string,
    menuItem: UpdateMenuItemInput
  ): Promise<Vendor> {
    try {
      const vendor = await apiClient.put<Record<string, unknown>>(
        `${this.basePath}/${vendorId}/menu-items/${itemId}`,
        menuItem
      );
      return mapVendor(vendor);
    } catch (error) {
      console.error('Error updating menu item:', error);
      throw error;
    }
  }

  async deleteMenuItem(vendorId: string, itemId: string): Promise<Vendor> {
    try {
      const vendor = await apiClient.delete<Record<string, unknown>>(
        `${this.basePath}/${vendorId}/menu-items/${itemId}`
      );
      return mapVendor(vendor);
    } catch (error) {
      console.error('Error deleting menu item:', error);
      throw error;
    }
  }

  async bulkImportMenuItems(vendorId: string, menuItems: CreateMenuItemInput[]): Promise<Vendor> {
    try {
      const vendor = await apiClient.post<Record<string, unknown>>(
        `${this.basePath}/${vendorId}/menu-items/bulk`,
        { menuItems }
      );
      return mapVendor(vendor);
    } catch (error) {
      console.error('Error bulk importing menu items:', error);
      throw error;
    }
  }

  async bulkUpdateAvailability(vendorId: string, itemIds: string[], available: boolean): Promise<void> {
    await apiClient.put(`${this.basePath}/${vendorId}/menu-items/bulk-availability`, {
      itemIds,
      available,
    });
  }

  async exportMenuItemsCsv(vendorId: string): Promise<Blob> {
    try {
      return await apiClient.getBlob(`${this.basePath}/${vendorId}/menu-items/export`);
    } catch (error) {
      console.error('Error exporting menu items CSV:', error);
      throw error;
    }
  }
}

const vendorService = new VendorService();
export default vendorService;
